

import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  TextField,
  Typography,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import { Download } from "@mui/icons-material";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { GetApprovedSuppliers, SupplierLedgerApi } from "../../../Services/Services";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const CreditorLedger = () => {
  const [creditors, setCreditors] = useState([]);
  const [selectedCreditor, setSelectedCreditor] = useState(null);
  const [ledgerData, setLedgerData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch Creditors on mount
  useEffect(() => {
    fetchCreditorsData();
  }, []);

  // ✅ Auto fetch ledger when creditor is selected
  useEffect(() => {
    if (selectedCreditor) {
      fetchLedgerData();
    }
  }, [selectedCreditor]);

  // Fetch Creditors/Suppliers
  const fetchCreditorsData = async () => {
    try {
      const response = await GetApprovedSuppliers();

      setCreditors(response.results || response || []);
    } catch (error) {
      console.error("Error fetching Creditors:", error);
      // toast.error("Failed to load creditors");
    }
  };

  // Fetch Ledger Data
  const fetchLedgerData = async () => {
    if (!selectedCreditor) {
      return;
    }

    setLoading(true);

    try {
 
      const response = await SupplierLedgerApi(selectedCreditor.id, {});



      if (response.success) {
        setLedgerData(response.entries || []);
        setSummary(response.summary || null);

        if (response.entries && response.entries.length > 0) {
          // toast.success(
          //   `✅ Ledger loaded successfully with ${response.entries.length} entries`
          // );
        } else {
          // toast.info("ℹ️ No ledger entries found for this creditor");
        }
      } else {
        setLedgerData([]);
        setSummary(null);
        // toast.error(response.message || "Failed to fetch ledger");
      }
    } catch (error) {
      console.error("Error fetching ledger data:", error);
      const errorMessage = error.message || "Failed to load ledger data";
      setLedgerData([]);
      setSummary(null);
      // toast.error(`❌ ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle Creditor Selection Change
  const handleCreditorChange = (event, newValue) => {


    setSelectedCreditor(newValue);

    // Clear previous data when creditor changes
    setLedgerData([]);
    setSummary(null);

    if (newValue) {
      // toast.info(`Selected: ${newValue.name}`);
    }
  };

  // Currency Formatter
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  // Excel Export Function
  const handleExportExcel = async () => {
    if (!ledgerData || ledgerData.length === 0) {
      // toast.warning("⚠️ No data available to export");
      return;
    }

    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Creditor Ledger");

      // Add Title
      worksheet.mergeCells("A1:H1");
      worksheet.getCell("A1").value = `Creditor Ledger - ${
        summary?.supplier_name || ""
      }`;
      worksheet.getCell("A1").font = { size: 16, bold: true };
      worksheet.getCell("A1").alignment = { horizontal: "center" };

      // Add Creditor Info
      worksheet.mergeCells("A2:H2");
      worksheet.getCell("A2").value = `Contact: ${
        summary?.contact || "N/A"
      } | Email: ${summary?.email || "N/A"} | GST: ${summary?.gst || "N/A"}`;
      worksheet.getCell("A2").font = { size: 11 };
      worksheet.getCell("A2").alignment = { horizontal: "center" };

      // Add Bank Info
      if (summary?.bank_name) {
        worksheet.mergeCells("A3:H3");
        worksheet.getCell("A3").value = `Bank: ${
          summary?.bank_name || "N/A"
        } | Account: ${summary?.account_no || "N/A"} | IFSC: ${
          summary?.ifsc_code || "N/A"
        }`;
        worksheet.getCell("A3").font = { size: 10 };
        worksheet.getCell("A3").alignment = { horizontal: "center" };
      }

      worksheet.addRow([]);

      // Add Summary
      worksheet.addRow([
        "Opening Balance:",
        summary?.opening_balance || 0,
        "",
        "",
        "Closing Balance:",
        summary?.closing_balance || 0,
      ]);
      worksheet.addRow([
        "Total Debit:",
        summary?.total_debit || 0,
        "",
        "",
        "Total Credit:",
        summary?.total_credit || 0,
      ]);
      worksheet.addRow([]);

      // Define Columns
      const headerRowIndex = worksheet.rowCount + 1;
      worksheet.columns = [
        { header: "S.No", key: "sno", width: 8 },
        { header: "Date", key: "date", width: 12 },
        { header: "Voucher Type", key: "voucherType", width: 15 },
        { header: "Voucher No", key: "voucherId", width: 15 },
        { header: "Description", key: "description", width: 35 },
        { header: "Debit (₹)", key: "debit", width: 15 },
        { header: "Credit (₹)", key: "credit", width: 15 },
        { header: "Balance (₹)", key: "balance", width: 15 },
      ];

      // Apply Header Style
      const headerRow = worksheet.getRow(headerRowIndex);
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF4472C4" },
        };
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
        cell.alignment = { horizontal: "center", vertical: "middle" };
      });

      // Add Data Rows
      ledgerData.forEach((entry, index) => {
        const row = worksheet.addRow({
          sno: index + 1,
          date: entry.date,
          voucherType: entry.voucher_type,
          voucherId: entry.voucher_number,
          description: entry.description || "-",
          debit: entry.debit || 0,
          credit: entry.credit || 0,
          balance: entry.balance || 0,
        });

        // Apply borders
        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
          cell.alignment = { vertical: "middle" };
        });

        // Number formatting
        row.getCell(6).numFmt = "₹#,##0.00";
        row.getCell(7).numFmt = "₹#,##0.00";
        row.getCell(8).numFmt = "₹#,##0.00";

        // Color coding for balance
        const balanceCell = row.getCell(8);
        if (entry.balance >= 0) {
          balanceCell.font = { color: { argb: "FF008000" }, bold: true };
        } else {
          balanceCell.font = { color: { argb: "FFFF0000" }, bold: true };
        }
      });

      // Save Excel File
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer]),
        `Creditor_Ledger_${selectedCreditor?.name || "Export"}_${
          new Date().toISOString().split("T")[0]
        }.xlsx`
      );

      // toast.success("✅ Excel exported successfully!");
    } catch (error) {
      console.error("Export error:", error);
      // toast.error("❌ Failed to export Excel");
    }
  };

  return (
    <Box p={2}>
      {/* Header with Creditor dropdown */}
      <Box display="flex" gap={2} mb={2} sx={{ p: 0, pt: 0, mt: -4 }}>
        <Autocomplete
          options={creditors}
          getOptionLabel={(option) =>
            `${option.name} ${option.bank_name ? `- ${option.bank_name}` : ""}`
          }
          value={selectedCreditor}
          onChange={handleCreditorChange}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Creditor/Supplier"
              sx={{
                height: 35,
                minWidth: 250,
                display: "flex",
                alignItems: "center",
                fontSize: "0.7rem",
                borderRadius: "0",
                "& .MuiInputBase-root": {
                  height: 35,
                  fontSize: "0.7rem",
                  borderRadius: 0,
                  padding: "0 6px",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem",
                  top: -3,
                },
              }}
            />
          )}
          fullWidth
        />

        <IconButton
          size="small"
          color="success"
          onClick={handleExportExcel}
          disabled={!ledgerData.length}
          title="Export to Excel"
        >
          <Download fontSize="small" />
        </IconButton>
      </Box>

      {/* ✅ Summary Cards - Always Visible with Reduced Height */}
      <Box display="flex" gap={5} mb={2} flexWrap="wrap">
        <Box
          p={0.8}
          border="2px solid #e0e0e0"
          borderRadius={1}
          minWidth={250}
          bgcolor="#f5f5f5"
        >
          <Typography variant="caption" color="text.secondary" fontWeight="bold" fontSize="0.65rem">
            Opening Balance
          </Typography>
          <Typography variant="body1" fontWeight="bold" fontSize="0.95rem">
            {formatCurrency(summary?.opening_balance || 0)}
          </Typography>
        </Box>

        <Box
          p={0.8}
          border="2px solid #4caf50"
          borderRadius={1}
          minWidth={250}
          bgcolor="#e8f5e9"
        >
          <Typography variant="caption" color="success.main" fontWeight="bold" fontSize="0.65rem">
            Total Debit
          </Typography>
          <Typography variant="body1" fontWeight="bold" color="success.main" fontSize="0.95rem">
            {formatCurrency(summary?.total_debit || 0)}
          </Typography>
        </Box>

        <Box
          p={0.8}
          border="2px solid #f44336"
          borderRadius={1}
          minWidth={250}
          bgcolor="#ffebee"
        >
          <Typography variant="caption" color="error.main" fontWeight="bold" fontSize="0.65rem">
            Total Credit
          </Typography>
          <Typography variant="body1" fontWeight="bold" color="error.main" fontSize="0.95rem">
            {formatCurrency(summary?.total_credit || 0)}
          </Typography>
        </Box>

        <Box
          p={0.8}
          border={`2px solid ${
            (summary?.closing_balance || 0) >= 0 ? "#4caf50" : "#f44336"
          }`}
          borderRadius={1}
          minWidth={250}
          bgcolor={(summary?.closing_balance || 0) >= 0 ? "#e8f5e9" : "#ffebee"}
        >
          <Typography
            variant="caption"
            color={(summary?.closing_balance || 0) >= 0 ? "success.main" : "error.main"}
            fontWeight="bold"
            fontSize="0.65rem"
          >
            Closing Balance
          </Typography>
          <Typography
            variant="body1"
            fontWeight="bold"
            color={(summary?.closing_balance || 0) >= 0 ? "success.main" : "error.main"}
            fontSize="0.95rem"
          >
            {formatCurrency(summary?.closing_balance || 0)}
          </Typography>
          {/* <Typography variant="caption" color="text.secondary" fontSize="0.6rem">
            Entries: {summary?.total_entries || 0}
          </Typography> */}
        </Box>
      </Box>

      {/* ✅ Table - Always Visible with Header */}
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: 400,
          overflowX: "auto",
          border: "1px solid #e0e0e0",
          borderRadius: 1,
        }}
      >
        <Table
          size="small"
          stickyHeader
          sx={{
            minWidth: 1000,
            "& td, & th": {
              textAlign: "center",
              whiteSpace: "nowrap",
              borderBottom: "1px solid #ccc",
            },
          }}
        >
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: "#f5f5f5",
                "& th": {
                  fontWeight: "bold",
                  color: "#333",
                  borderBottom: "2px solid #999",
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              <TableCell>S.No.</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Voucher Type</TableCell>
              <TableCell>Voucher No</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Debit</TableCell>
              <TableCell>Credit</TableCell>
              <TableCell>Balance</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Loading State */}
      
            {/* Creditor Selected but No Data */}
            {!loading && selectedCreditor && ledgerData.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} sx={{ py: 4, textAlign: "center" }}>
                  <Typography color="text.secondary">
                    No ledger entries found for this creditor
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {/* Data Rows */}
            {!loading && ledgerData.length > 0 && ledgerData.map((entry, index) => (
              <TableRow
                hover
                key={entry.id}
                sx={{
                  backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                  "& td": {
                    paddingTop: "4px !important",
                    paddingBottom: "4px !important",
                    fontSize: "0.8125rem",
                  },
                  "&:hover": {
                    backgroundColor: "#e3f2fd !important",
                  },
                }}
              >
                <TableCell>{index + 1}</TableCell>
                <TableCell>{entry.date}</TableCell>
                <TableCell>{entry.voucher_type}</TableCell>
                <TableCell>{entry.voucher_number}</TableCell>
                <TableCell
                  sx={{
                    maxWidth: 200,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {entry.description || "-"}
                </TableCell>
                <TableCell
                  sx={{ color: entry.debit > 0 ? "error.main" : "text.disabled" }}
                >
                  {entry.debit > 0 ? formatCurrency(entry.debit) : "-"}
                </TableCell>
                <TableCell
                  sx={{ color: entry.credit > 0 ? "success.main" : "text.disabled" }}
                >
                  {entry.credit > 0 ? formatCurrency(entry.credit) : "-"}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    color: entry.balance >= 0 ? "success.main" : "error.main",
                  }}
                >
                  {formatCurrency(entry.balance)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CreditorLedger;